import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { Link } from 'react-router-dom';
import { AuthData, withAuthContext } from '../context/auth';
import {
	CLAIMS_TABLE_HEADER_CREATED_DATE,
	CLAIMS_TABLE_HEADER_ID,
	CLAIMS_TABLE_HEADER_INCIDENT_DATE,
	CLAIMS_TABLE_HEADER_NOTIFICATION_DATE,
	CLAIMS_TABLE_HEADER_STATUS,
	CLAIMS_TABLE_HEADER_UPDATED_DATE,
} from '../data/labelsAndMessages';
import { ClaimResponse } from '../types/claim';
import { tableStyles } from '../util/styles';
import { getComparator, HeadCell, Order, stableSort } from '../util/tableUtil';
import { capitalize } from '../util/util';
import { StyledEmptyMessage } from './common/index';
import SortableTable from './SortableTable';

interface Props {
	claims: ClaimResponse[];
	authData: AuthData;
}

interface Data {
	claimId: number;
	dateCreated: string;
	lastUpdated: string;
	incidentDate: string;
	notificationDate: string;
	status: string;
	link: string;
}

function createData(
	claimId: number,
	dateCreated: string,
	lastUpdated: string,
	incidentDate: string,
	notificationDate: string,
	status: string,
	link: string,
): Data {
	return {
		claimId,
		dateCreated,
		lastUpdated,
		incidentDate,
		notificationDate,
		status,
		link,
	};
}

const ClaimsTable = (props: Props) => {
	const { claims, authData } = props;
	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<string>('claimId');

	const emptyTable = !claims.length ? (
		<StyledEmptyMessage>No Claims</StyledEmptyMessage>
	) : null;
	const classes = tableStyles();

	const headCells: HeadCell[] = [
		{ id: 'claimId', rightAlign: false, label: CLAIMS_TABLE_HEADER_ID() },
		{
			id: 'dateCreated',
			rightAlign: false,
			label: CLAIMS_TABLE_HEADER_CREATED_DATE(),
		},
		{
			id: 'lastUpdated',
			rightAlign: false,
			label: CLAIMS_TABLE_HEADER_UPDATED_DATE(),
		},
		{
			id: 'incidentDate',
			rightAlign: false,
			label: CLAIMS_TABLE_HEADER_INCIDENT_DATE(),
		},
		{
			id: 'notificationDate',
			rightAlign: false,
			label: CLAIMS_TABLE_HEADER_NOTIFICATION_DATE(),
		},
		{ id: 'status', rightAlign: false, label: CLAIMS_TABLE_HEADER_STATUS() },
	];

	const rows: any = claims.map((claim: any, index: number) => {
		return createData(
			claim.locator,
			authData.getFormattedDate(claim.createdTimestamp),
			authData.getFormattedDate(claim.updatedTimestamp),
			authData.getFormattedDate(claim.incidentTimestamp),
			authData.getFormattedDate(claim.notificationTimestamp),
			capitalize(claim.currentStatus),
			`/policy/${claim.policyLocator}/claims/${claim.locator}`,
		);
	});

	const rowElements = stableSort(rows, getComparator(order, orderBy)).map(
		(row: any) => {
			return (
				<TableRow
					component={Link}
					to={row.link}
					hover
					tabIndex={-1}
					key={row.claimId}
					className={classes.tableRow}
				>
					<TableCell id={row.policyId} scope='row' padding='none'>
						{row.claimId}
					</TableCell>
					<TableCell>{row.dateCreated}</TableCell>
					<TableCell>{row.lastUpdated}</TableCell>
					<TableCell>{row.incidentDate}</TableCell>
					<TableCell>{row.notificationDate}</TableCell>
					<TableCell>{row.status}</TableCell>
				</TableRow>
			);
		},
	);

	return (
		<SortableTable
			rows={rowElements}
			headCells={headCells}
			additionalMsg={emptyTable}
			order={order}
			setOrder={setOrder}
			orderBy={orderBy}
			setOrderBy={setOrderBy}
		/>
	);
};

export default withAuthContext<Props>(ClaimsTable);
